import {useEffect} from "react";

declare global {
  interface Window {
    jQuery?: {
      fancybox?: {
        open: (items: unknown, options?: Record<string, unknown>, index?: number) => void;
      };
      fn: {
        fancybox?: (options?: Record<string, unknown>) => unknown;
      };
    };
  }
}

let loadPromise: Promise<void> | null = null;

function loadStylesheet(href: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if(document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load ${href}`));
    document.head.appendChild(link);
  });
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if(document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

export function loadFancybox(): Promise<void> {
  if(typeof window === "undefined") return Promise.resolve();
  if(window.jQuery?.fancybox?.open) return Promise.resolve();
  if(loadPromise) return loadPromise;

  loadPromise = loadStylesheet("https://cdn.jsdelivr.net/npm/@fancyapps/fancybox@3.5.7/dist/jquery.fancybox.min.css")
    .then(() => loadScript("https://cdn.jsdelivr.net/npm/jquery@3.7.1/dist/jquery.min.js"))
    .then(() => loadScript("https://cdn.jsdelivr.net/npm/@fancyapps/fancybox@3.5.7/dist/jquery.fancybox.min.js"));

  return loadPromise;
}

const FANCYBOX_OPTS: Record<string, unknown> = {
  loop: true,
  infobar: false,
  toolbar: true,
  buttons: ["slideShow", "fullScreen", "thumbs", "close"],
  animationEffect: "fade",
  transitionEffect: "slide",
  protect: true,
  parentEl: "body",
  hash: false,
  clickContent: false,
  clickSlide: "close",
  mobile: {clickContent: "close", clickSlide: "close"}
};

/** Preload Fancybox on PDP mount. */
export function useFancyboxGallery(active: boolean) {
  useEffect(() => {
    if(!active) return;
    loadFancybox().catch(() => {});
  }, [active]);
}

export function openProductGallery(images: string[], startIndex = 0, caption = "") {
  return loadFancybox().then(() => {
    const fb = window.jQuery?.fancybox;
    if(!fb?.open || !images.length) return;
    fb.open(
      images.map((src) => ({src, type: "image", opts: {caption}})),
      FANCYBOX_OPTS,
      Math.max(0, Math.min(startIndex, images.length - 1))
    );
  });
}
